<template>
  <div class="campaign-card">
    <UiSmartLink
      class="campaign-card__link"
      :to="linkUrl"
      :rel="isOpenInNewTab ? 'noopener noreferrer' : null"
      :target="isOpenInNewTab ? '_blank' : null"
    >
      <UiSmartImage v-bind="smartImageAttrs" class="campaign-card__image" />
      <div class="campaign-card__content">
        <p class="campaign-card__title headline-m">{{ title }}</p>
        <p class="campaign-card__subtitle body-m" v-if="subtitle">
          {{ subtitle }}
        </p>
        <div class="campaign-card__actions">
          <UiMiniButton
            v-if="ctaText"
            :text="ctaText"
            class="campaign-card__cta"
          />
          <slot>
            <button
              v-if="tnc"
              @click.prevent="isModalOpen = !isModalOpen"
              class="campaign-card__tnc body-s"
            >
              {{ tnc.label }}
            </button>
          </slot>
        </div>
      </div>
    </UiSmartLink>

    <UiModal
      v-if="isModalOpen"
      :title="tnc?.label"
      @close="isModalOpen = false"
    >
      <template #body> {{ tnc?.content }} </template>
    </UiModal>
  </div>
</template>

<script lang="ts" setup>
import type { PropsCampaignCard } from './types'
import UiSmartLink from '@sephora-asia/ui-smart-link'
import UiModal from '@sephora-asia/ui-modal'
import UiMiniButton from '@sephora-asia/ui-mini-button'
import UiSmartImage, {
  type PropsSmartImage,
} from '@sephora-asia/ui-smart-image'
import { computed, ref } from 'vue'

const props = withDefaults(defineProps<PropsCampaignCard>(), {
  isOpenInNewTab: false,
})

const isModalOpen = ref(false)

const smartImageAttrs = computed(
  (): PropsSmartImage => ({
    ...props.image,
    aspectRatio: props.image?.aspectRatio ?? '4/3',
    alt: props.title,
  })
)
</script>

<style lang="scss" scoped>
.campaign-card {
  width: 232px;
  border-radius: var(--size-radius-medium);
  border: 1px solid var(--color-base-grey-10);
  overflow: hidden;

  &__link {
    text-decoration: none;
    color: var(--color-base-black);
  }

  &__title {
    @include lineclamp(2);
  }

  &__subtitle {
    @include lineclamp(3);
  }

  &__actions {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
  }

  &__tnc {
    border: none;
    background-color: transparent;
    cursor: pointer;
    text-decoration: underline;
    padding: 0;
  }

  &__content {
    height: 169px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
  }

  @include media(desktop, widescreen, fullhd) {
    width: 256px;
  }
}
</style>
